import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getProgramFileContent } from '@/api/programFiles';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import type { ProgramFileInfo } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';

interface ProgramFilePreviewDialogProps {
  file: ProgramFileInfo;
  trigger: React.ReactElement;
}

export const ProgramFilePreviewDialog = ({ file, trigger }: ProgramFilePreviewDialogProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getProgramFileContent(file.id)
      .then((text) => {
        if (!cancelled) setContent(text);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(apiErrorMessage(e, t`Failed to load file preview.`));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, file.id]);

  const handleOpenChange = (newOpen: boolean): void => {
    setOpen(newOpen);
    if (!newOpen) {
      setContent(null);
      setError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger render={trigger} />
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{file.original_filename}</DialogTitle>
        </DialogHeader>

        <div>
          <div className="-mx-4 max-h-[70vh] overflow-auto px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-sm">
                  <Trans>Loading…</Trans>
                </span>
              </div>
            ) : error !== null ? (
              <div className="flex items-center gap-2 py-8 text-red-600">
                <AlertCircle size={16} />
                <span className="text-sm">{error}</span>
              </div>
            ) : content !== null ? (
              <pre className="font-mono text-xs text-slate-800 whitespace-pre">{content}</pre>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              <Trans>Close</Trans>
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
